import React, { useState, useEffect, useRef } from "react";
import { usePFDataContext } from "../../context/PfContext";
import { set } from "lodash";
// isSpecialPromo
const BrandCategoryStrain = () => {
  const { pfData, updatePfData } = usePFDataContext();

  // Selected filters
  const brandFilter = pfData.filter.brand || [];
  const categoryFilter = pfData.filter.category || [];
  const strainFilter = pfData.filter.strain || [];

  // Local state for filtered options
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [filteredStrains, setFilteredStrains] = useState([]);

  // Dropdown states
  const [brandDropdown, setBrandDropdown] = useState(false);
  const [categoryDropdown, setCategoryDropdown] = useState(false);
  const [strainDropdown, setStrainDropdown] = useState(false);

  // superSpecials
  const [superSpecials, setSuperSpecials] = useState(false);

  // Dropdown refs
  const brandDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const strainDropdownRef = useRef(null);



  // Handle filter changes and apply instantly
  const handleFilterChange = (filterKey, value, isChecked) => {
    const updatedValues = isChecked
      ? [...pfData.filter[filterKey], value]
      : pfData.filter[filterKey].filter((item) => item !== value);

    updatePfData(filterKey, updatedValues); // Apply instantly
  };

  // Dynamically compute filtered options whenever filters change
  useEffect(() => {
    const products = pfData.pfProducts;
    let updatedBrands = new Set(brandFilter);
    let updatedCategories = new Set(categoryFilter);
    let updatedStrains = new Set(strainFilter);

    const activeBrandFilter = brandFilter;
    const activeCategoryFilter = categoryFilter;
    const activeStrainFilter = strainFilter;

    let currentFilterKey = brandDropdown ? "brand" : categoryDropdown ? "category" : strainDropdown ? "strain" : null;

    Object.entries(products).forEach(([brand, brandData]) => {
      if (!brandData.categories) return;

      let brandMatches = activeBrandFilter.length === 0 || activeBrandFilter.includes(brand);
      let categoryMatches = false;
      let strainMatches = false;

      Object.entries(brandData.categories).forEach(([category, productList]) => {
        let categoryValid = activeCategoryFilter.length === 0 || activeCategoryFilter.includes(category);

        if (brandMatches && categoryValid) {
          const matchingProducts = productList.filter((product) => {
            return activeStrainFilter.length === 0 || (product.strain && activeStrainFilter.includes(product.strain.toLowerCase()));
          });

          if (matchingProducts.length > 0) {
            updatedBrands.add(brand);
            updatedCategories.add(category);

            matchingProducts.forEach((product) => {
              if (product.strain) {
                updatedStrains.add(product.strain.toLowerCase());
              }
            });

            categoryMatches = true;
            strainMatches = true;
          }
        }
      });

      if (brandMatches && (categoryMatches || strainMatches)) {
        updatedBrands.add(brand);
      }
    });

    // Update state only if the current filter key is NOT the one being modified
    if (currentFilterKey !== "brand") setFilteredBrands([...updatedBrands].sort());
    if (currentFilterKey !== "category") setFilteredCategories([...updatedCategories].sort());
    if (currentFilterKey !== "strain") setFilteredStrains([...updatedStrains].sort());
  }, [pfData.filter, brandDropdown, categoryDropdown, strainDropdown]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(event.target)) {
        setBrandDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdown(false);
      }
      if (strainDropdownRef.current && !strainDropdownRef.current.contains(event.target)) {
        setStrainDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Corrected showSuperSpecials function
  const showSuperSpecials = () => {
    setSuperSpecials((prevSuperSpecials) => {
      const newSuperSpecialsState = !prevSuperSpecials;

      if (newSuperSpecialsState) {
        updatePfData("isSpecialPromo", true);
      } else {
        updatePfData("isSpecialPromo", false);
      }

      return newSuperSpecialsState; // Update the state correctly
    });
  };

  return (
    <>
      {/* Brand Filter */}
      <div className="product__listing--filter" ref={brandDropdownRef}>
        <div className="select__widget">
          <div className="select__widget--selected-options select__widget--selected-options--brand" onClick={() => setBrandDropdown(!brandDropdown)}>
            {brandFilter.length > 0 ? brandFilter.join(", ") : "Filter by Brand"}
          </div>
          {brandDropdown && (
            <ul className="select__dopdown select__dopdown-brand--filter">
              {filteredBrands.map((brand) => (
                <li key={brand}>
                  <label>
                    <input
                      type="checkbox"
                      name="brand[]"
                      value={brand}
                      className="select__brand"
                      onChange={(e) => handleFilterChange("brand", e.target.value, e.target.checked)}
                      checked={brandFilter.includes(brand)}
                    />
                    <span className="check">✓</span> {brand}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Category Filter */}
      <div className="product__listing--filter" ref={categoryDropdownRef}>
        <div className="select__widget">
          <div className="select__widget--selected-options select__widget--selected-options--category" onClick={() => setCategoryDropdown(!categoryDropdown)}>
            {categoryFilter.length > 0 ? categoryFilter.join(", ") : "Filter by Category"}
          </div>
          {categoryDropdown && (
            <ul className="select__dopdown select__dopdown-category--filter">
              {filteredCategories.map((category) => (
                <li key={category}>
                  <label>
                    <input
                      type="checkbox"
                      name="category[]"
                      value={category}
                      className="select__category"
                      onChange={(e) => handleFilterChange("category", e.target.value, e.target.checked)}
                      checked={categoryFilter.includes(category)}
                    />
                    <span className="check">✓</span> {category}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Strain Filter */}
      <div className="product__listing--filter" ref={strainDropdownRef}>
        <div className="select__widget">
          <div className="select__widget--selected-options select__widget--selected-options--strain" onClick={() => setStrainDropdown(!strainDropdown)}>
            {strainFilter.length > 0 ? strainFilter.join(", ") : "Filter by Strain"}
          </div>
          {strainDropdown && (
            <ul className="select__dopdown select__dopdown-strain--filter">
              {filteredStrains.map((strain) => (
                <li key={strain}>
                  <label>
                    <input
                      type="checkbox"
                      name="strain[]"
                      value={strain}
                      className="select__strain"
                      onChange={(e) => handleFilterChange("strain", e.target.value, e.target.checked)}
                      checked={strainFilter.includes(strain)}
                    />
                    <span className="check">✓</span> {strain}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

       {/* Filter by Super Specials */}
       <div className="product__listing--filter" ref={strainDropdownRef}>
          <button 
            style={{ padding: "3px 15px", minHeight: "30px", textTransform: "capitalize" }}
            className={`btn btn--primary ${superSpecials ? ' showSpecials ' : ''}`} onClick={() => showSuperSpecials()}>
            Filter by Super Specials
          </button>
      </div>
      
    </>
  );
};

export default BrandCategoryStrain;
